/**
 * Composant pour gérer les contacts d'un client
 * Dashboard Design V2: Glassmorphism avec transparences
 * NOTE: Ce composant est affiché DANS une modal parent - pas de modal imbriquée
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { clientsAPI } from '@/utils/clientsAPI';
import type { ClientContact, ContactType } from '@/types/models';
import { notifications } from '@/lib/notifications';
import { confirm } from '@/stores/confirmStore';
import { logger } from '@/utils/logger';
import { Users, UserPlus, Star, Mail, Phone, Building, MapPin, FileText, Trash2, Edit3, ToggleLeft, ToggleRight, ArrowLeft, Check } from 'lucide-react';

interface ClientContactsManagerProps {
  clientId: string;
  clientName: string;
}

const CONTACT_TYPE_KEYS: Record<ContactType, string> = {
  PRIMARY: 'clients.contacts.types.PRIMARY',
  BILLING: 'clients.contacts.types.BILLING',
  COMMERCIAL: 'clients.contacts.types.COMMERCIAL',
  TECHNICAL: 'clients.contacts.types.TECHNICAL',
  LEGAL: 'clients.contacts.types.LEGAL',
  OTHER: 'clients.contacts.types.OTHER',
};

const CONTACT_TYPE_COLORS: Record<ContactType, string> = {
  PRIMARY: 'bg-teal-500/20 text-teal-700 border border-teal-500/30',
  BILLING: 'bg-green-500/20 text-green-700 border border-green-500/30',
  COMMERCIAL: 'bg-purple-500/20 text-purple-700 border border-purple-500/30',
  TECHNICAL: 'bg-orange-500/20 text-orange-700 border border-orange-500/30',
  LEGAL: 'bg-red-500/20 text-red-700 border border-red-500/30',
  OTHER: 'bg-gray-500/20 text-gray-700 border border-gray-500/30',
};

type ViewMode = 'list' | 'form';

export function ClientContactsManager({ clientId, clientName }: ClientContactsManagerProps) {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState<ClientContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingContact, setEditingContact] = useState<ClientContact | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    type: 'PRIMARY' as ContactType,
    fullName: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    city: '',
    isPrimary: false,
    notes: '',
  });

  useEffect(() => {
    loadContacts();
  }, [clientId, showInactive]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const data = await clientsAPI.getContacts(clientId, !showInactive);
      setContacts(data);
    } catch (error) {
      logger.error('Erreur lors du chargement des contacts:', error);
      notifications.error(t('clients.contacts.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleShowForm = (contact?: ClientContact) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        type: contact.type,
        fullName: contact.fullName,
        email: contact.email,
        phone: contact.phone || '',
        position: contact.position || '',
        department: contact.department || '',
        city: contact.city || '',
        isPrimary: contact.isPrimary,
        notes: contact.notes || '',
      });
    } else {
      setEditingContact(null);
      setFormData({
        type: 'PRIMARY',
        fullName: '',
        email: '',
        phone: '',
        position: '',
        department: '',
        city: '',
        isPrimary: false,
        notes: '',
      });
    }
    setViewMode('form');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setEditingContact(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingContact) {
        await clientsAPI.updateContact(clientId, editingContact.id, formData);
        notifications.success(t('clients.contacts.updateSuccess'));
      } else {
        await clientsAPI.createContact(clientId, formData);
        notifications.success(t('clients.contacts.createSuccess'));
      }
      handleBackToList();
      loadContacts();
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde du contact:', error);
      notifications.error(t('clients.contacts.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (contactId: string, contactName: string) => {
    const confirmed = await confirm({
      title: t('clients.contacts.deleteTitle'),
      message: t('clients.contacts.deleteConfirm', { name: contactName }),
      variant: 'danger',
      confirmText: t('clients.contacts.deleteTitle'),
      cancelText: t('clients.contacts.cancel'),
    });
    if (!confirmed) {
      return;
    }

    try {
      await clientsAPI.deleteContact(clientId, contactId);
      notifications.success(t('clients.contacts.deleteSuccess'));
      loadContacts();
    } catch (error) {
      logger.error('Erreur lors de la suppression du contact:', error);
      notifications.error(t('clients.contacts.deleteError'));
    }
  };

  const handleSetPrimary = async (contactId: string) => {
    try {
      await clientsAPI.setPrimaryContact(clientId, contactId);
      notifications.success(t('clients.contacts.setPrimarySuccess'));
      loadContacts();
    } catch (error) {
      logger.error('Erreur lors de la définition du contact principal:', error);
      notifications.error(t('clients.contacts.setPrimaryError'));
    }
  };

  const handleToggleStatus = async (contactId: string) => {
    try {
      await clientsAPI.toggleContactStatus(clientId, contactId);
      loadContacts();
    } catch (error) {
      logger.error('Erreur lors du changement de statut:', error);
      notifications.error(t('clients.contacts.statusError'));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent"></div>
      </div>
    );
  }

  // ===== MODE FORMULAIRE (inline, design V2 avec transparences) =====
  if (viewMode === 'form') {
    return (
      <div className="space-y-6">
        {/* Header avec bouton retour */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackToList}
            className="p-2 hover:bg-white/30 rounded-xl transition-all text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-500/20 backdrop-blur-sm flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {editingContact ? t('clients.contacts.edit') : t('clients.contacts.new')}
              </h2>
              <p className="text-sm text-gray-500">
                {editingContact ? t('clients.contacts.updateInfo') : t('clients.contacts.addFor', { name: clientName })}
              </p>
            </div>
          </div>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Informations principales */}
          <div className="bg-white/40 backdrop-blur-sm border border-white/50 rounded-2xl p-5 space-y-4">
            <h4 className="text-sm font-semibold text-teal-700 uppercase tracking-wider flex items-center gap-2">
              <Users className="w-4 h-4" />
              {t('clients.contacts.mainInfo')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('clients.contacts.fullName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-white/50 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all"
                  placeholder={t('clients.contacts.placeholders.fullName')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('clients.fields.email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-white/50 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all"
                  placeholder={t('clients.contacts.placeholders.email')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('clients.contacts.contactType')}
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as ContactType })}
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-white/50 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all"
                >
                  {Object.entries(CONTACT_TYPE_KEYS).map(([value, key]) => (
                    <option key={value} value={value}>
                      {t(key)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('clients.fields.phone')}
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-white/50 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all"
                  placeholder={t('clients.contacts.placeholders.phone')}
                />
              </div>
            </div>
          </div>

          {/* Informations professionnelles */}
          <div className="bg-white/40 backdrop-blur-sm border border-white/50 rounded-2xl p-5 space-y-4">
            <h4 className="text-sm font-semibold text-teal-700 uppercase tracking-wider flex items-center gap-2">
              <Building className="w-4 h-4" />
              {t('clients.contacts.professionalInfo')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('clients.contacts.position')}
                </label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-white/50 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all"
                  placeholder={t('clients.contacts.placeholders.position')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('clients.contacts.department')}
                </label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-white/50 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all"
                  placeholder={t('clients.contacts.placeholders.department')}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('clients.contacts.cityMultisite')}
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-white/50 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all"
                  placeholder={t('clients.contacts.placeholders.city')}
                />
              </div>
            </div>
          </div>

          {/* Notes et options */}
          <div className="bg-white/40 backdrop-blur-sm border border-white/50 rounded-2xl p-5 space-y-4">
            <h4 className="text-sm font-semibold text-teal-700 uppercase tracking-wider flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t('clients.contacts.notesAndOptions')}
            </h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('clients.contacts.internalNotes')}
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-white/50 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all resize-none"
                placeholder={t('clients.contacts.notesPlaceholder')}
              />
            </div>

            <div className="flex items-start gap-3 p-4 bg-yellow-500/10 backdrop-blur-sm border border-yellow-500/30 rounded-xl">
              <input
                type="checkbox"
                id="isPrimary"
                checked={formData.isPrimary}
                onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                className="mt-1 w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <label htmlFor="isPrimary" className="cursor-pointer">
                <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-600" />
                  {t('clients.contacts.setPrimaryTitle')}
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  {t('clients.contacts.setPrimaryDescription')}
                </p>
              </label>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/30">
            <button
              type="button"
              onClick={handleBackToList}
              disabled={submitting}
              className="px-6 py-3 text-gray-700 font-medium rounded-xl hover:bg-white/30 transition-all duration-300"
            >
              {t('clients.contacts.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 shadow-lg shadow-teal-600/20"
            >
              {submitting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {editingContact ? t('clients.contacts.save') : t('clients.contacts.createContact')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ===== MODE LISTE (design V2 avec transparences) =====
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-teal-500/20 backdrop-blur-sm flex items-center justify-center">
            <Users className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {t('clients.contacts.contactsOf', { name: clientName })}
            </h2>
            <p className="text-sm text-gray-500">
              {t('clients.contacts.contactCount', { count: contacts.length })}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer px-3 py-2 bg-white/30 rounded-xl hover:bg-white/40 transition-all">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            />
            {t('clients.contacts.showInactive')}
          </label>
          <button
            onClick={() => handleShowForm()}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-teal-600/20"
          >
            <UserPlus className="w-4 h-4" />
            {t('clients.contacts.new')}
          </button>
        </div>
      </div>

      {/* Contacts List */}
      {contacts.length === 0 ? (
        <div className="text-center py-12 bg-white/30 backdrop-blur-sm rounded-2xl border border-white/40">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/40 flex items-center justify-center">
            <Users className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500 mb-4">{t('clients.contacts.noContactsFound')}</p>
          <button
            onClick={() => handleShowForm()}
            className="text-teal-600 hover:text-teal-700 font-medium transition-colors"
          >
            {t('clients.contacts.createFirst')}
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {contacts.map((contact) => (
            <div
              key={contact.id}
              className={`bg-white/50 backdrop-blur-sm border border-white/40 rounded-2xl p-5 transition-all duration-300 hover:bg-white/60 hover:border-teal-500/30 hover:shadow-lg ${
                !contact.isActive ? 'opacity-50' : ''
              }`}
            >
              <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <h3 className="font-semibold text-lg text-gray-900">
                      {contact.fullName}
                    </h3>
                    <span
                      className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                        CONTACT_TYPE_COLORS[contact.type]
                      }`}
                    >
                      {t(CONTACT_TYPE_KEYS[contact.type])}
                    </span>
                    {contact.isPrimary && (
                      <span className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-yellow-500/20 text-yellow-700 border border-yellow-500/30">
                        <Star className="w-3 h-3" />
                        {t('clients.contacts.primary')}
                      </span>
                    )}
                    {!contact.isActive && (
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-500/20 text-gray-600 border border-gray-500/30">
                        {t('clients.contacts.inactive')}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {contact.email}
                    </div>
                    {contact.phone && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-4 h-4 text-gray-400" />
                        {contact.phone}
                      </div>
                    )}
                    {contact.position && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Building className="w-4 h-4 text-gray-400" />
                        {contact.position}
                      </div>
                    )}
                    {contact.department && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Building className="w-4 h-4 text-gray-400" />
                        {contact.department}
                      </div>
                    )}
                    {contact.city && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        {contact.city}
                      </div>
                    )}
                  </div>

                  {contact.notes && (
                    <div className="mt-3 flex items-start gap-2 text-sm text-gray-500">
                      <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                      {contact.notes}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {!contact.isPrimary && contact.isActive && (
                    <button
                      onClick={() => handleSetPrimary(contact.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-yellow-700 bg-yellow-500/10 border border-yellow-500/30 rounded-lg hover:bg-yellow-500/20 transition-colors"
                      title={t('clients.contacts.setPrimaryTitle')}
                    >
                      <Star className="w-3.5 h-3.5" />
                      {t('clients.contacts.primary')}
                    </button>
                  )}
                  <button
                    onClick={() => handleToggleStatus(contact.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      contact.isActive
                        ? 'text-gray-600 bg-white/40 border border-white/50 hover:bg-white/60'
                        : 'text-green-700 bg-green-500/10 border border-green-500/30 hover:bg-green-500/20'
                    }`}
                    title={contact.isActive ? t('clients.status.deactivate') : t('clients.contacts.activate')}
                  >
                    {contact.isActive ? (
                      <><ToggleRight className="w-3.5 h-3.5" /> {t('clients.contacts.active')}</>
                    ) : (
                      <><ToggleLeft className="w-3.5 h-3.5" /> {t('clients.contacts.activate')}</>
                    )}
                  </button>
                  <button
                    onClick={() => handleShowForm(contact)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-teal-700 bg-teal-500/10 border border-teal-500/30 rounded-lg hover:bg-teal-500/20 transition-colors"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    {t('clients.contacts.modify')}
                  </button>
                  <button
                    onClick={() => handleDelete(contact.id, contact.fullName)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-700 bg-red-500/10 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {t('clients.contacts.deleteTitle')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
